import { flatten } from 'lodash';

import {
  LocalPromptDependency,
  Prompt,
  PromptInput,
  PromptOutput,
} from '../../../../../../services/backendService/types';
import { notUndefined } from '../../../../../../utils/general';
import { Slide } from '../../../../../SlideComponents/slide.types';
import generateInputPromptNodes from './generateInputPromptNodes';
import generatePromptNodes from './generatePromptNodes';
import generatePromptOutputNodes from './generatePromptOutputNodes';
import generateSlideNodes from './generateSlideNodes';

export type PromptOutputAndNameId = PromptOutput & {
  promptName: string;
  promptId: string;
};

type GenerateAllNodesProps = {
  promptInputs: PromptInput[];
  prompts: Prompt[];
  promptOutputs: PromptOutputAndNameId[];
  slides: Slide[];
};

export const NODE_WIDTH = 150;
export const NODES_HORIZONTAL_GAP = 100;

export const flattenPromptOutputs = (
  promptOutputs: PromptOutputAndNameId[],
): LocalPromptDependency[] => {
  const flattedPromptOutputs: LocalPromptDependency[] = promptOutputs.flatMap(output => {
    if (output.type === 'string')
      return [
        {
          id: `${output.promptId}.outputText`,
          type: 'output',
          label: `${output.promptName}: Text`,
          formType: 'shortText',
        },
      ];

    if (output.type === 'array') return []; // not implemented

    if (output.type === 'object') {
      const outputFields = Object.entries(output.properties).map(([_, property]) => {
        if (property.type === 'array') {
          if (!property.items) return [];

          const fields = new Array(property.items.maxLength).fill(0).flatMap((_, i) => {
            if (!property.items) return undefined;
            if (!property.items.properties) {
              return [
                {
                  id: `${output.promptId}.${property.id}.${i}`,
                  type: 'output',
                  label: `${output.promptName}: ${property.description}.${
                    property.items.type === 'number' ? 'Number' : 'Text'
                  }.${i}`,
                  formType: 'shortText',
                } as LocalPromptDependency,
              ];
            }
            return Object.values(property.items.properties).map(itemProperty => {
              return {
                id: `${output.promptId}.${property.id}.${i}.${itemProperty.id}`,
                type: 'output',
                label: `${output.promptName}: ${property.description}.${itemProperty.description}.${i}`,
                formType: 'shortText',
              } as LocalPromptDependency;
            });
          });

          return fields.filter(notUndefined);
        }
        if (property.type === 'string' || property.type === 'number') {
          return [
            {
              id: `${output.promptId}.${property.id}`,
              type: 'output',
              label: `${output.promptName}: ${property.description}`,
              formType: 'shortText',
            } as LocalPromptDependency,
          ];
        }
        return []; // should never get here
      });
      return flatten(outputFields.filter(notUndefined));
    }

    return []; // Should never get here
  });

  return flattedPromptOutputs;
};

const generateAllNodes = ({
  promptInputs,
  prompts,
  promptOutputs,
  slides,
}: GenerateAllNodesProps) => {
  const flattedPromptOutputs = flattenPromptOutputs(promptOutputs);

  const largestRowOfNodes = Math.max(
    promptInputs.length,
    prompts.length,
    flattedPromptOutputs.length,
    slides.length,
  );
  const largestRowWidth =
    largestRowOfNodes * NODE_WIDTH + (largestRowOfNodes - 1) * NODES_HORIZONTAL_GAP;

  return [
    ...generateInputPromptNodes({ promptInputs, largestRowWidth }),
    ...generatePromptNodes({ prompts, largestRowWidth }),
    ...generatePromptOutputNodes({ flattedPromptOutputs, largestRowWidth }),
    ...generateSlideNodes({ slides, largestRowWidth }),
  ];
};

export default generateAllNodes;
